/**
 * Cloudflare Turnstile widget (explicit render)
 */

import React, { useEffect, useRef } from 'react'
import {
  isTurnstileEnabled,
  TURNSTILE_MESSAGES,
  TURNSTILE_SITE_KEY,
  TURNSTILE_WRAPPER_CLASS,
} from '../constants/turnstile.js'
import { loadTurnstileScript } from '../utils/turnstile.js'

export default function TurnstileWidget ({
  onTokenChange,
  onError,
  resetSignal = 0,
  action,
  theme = 'light',
  className = TURNSTILE_WRAPPER_CLASS,
}) {
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)
  const onTokenChangeRef = useRef(onTokenChange)
  const onErrorRef = useRef(onError)

  onTokenChangeRef.current = onTokenChange
  onErrorRef.current = onError

  useEffect(() => {
    onTokenChangeRef.current?.(null)
  }, [resetSignal])

  useEffect(() => {
    if (!isTurnstileEnabled() || !containerRef.current || !action) {
      return undefined
    }

    let cancelled = false

    const renderWidget = async () => {
      try {
        await loadTurnstileScript()
        if (cancelled || !containerRef.current || !window.turnstile) {
          return
        }

        if (widgetIdRef.current != null) {
          window.turnstile.remove(widgetIdRef.current)
          widgetIdRef.current = null
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          theme,
          callback: (t) => onTokenChangeRef.current?.(t),
          'expired-callback': () => onTokenChangeRef.current?.(null),
          'error-callback': () => {
            onTokenChangeRef.current?.(null)
            onErrorRef.current?.()
          },
        })
      } catch (err) {
        console.error('Turnstile render error:', err)
        onErrorRef.current?.()
      }
    }

    renderWidget()

    return () => {
      cancelled = true
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [resetSignal, action, theme])

  if (!isTurnstileEnabled()) {
    return (
      <p className="text-sm text-amber-800" role="status">
        {TURNSTILE_MESSAGES.NOT_CONFIGURED}
      </p>
    )
  }

  return (
    <div
      ref={containerRef}
      className={className}
      aria-label="Security verification"
    />
  )
}
